/**
 * useScopeTrainingEffectiveness — Round 76 (A-C-06 + A-C-07)
 *
 * Read-only training effectiveness + artifact intelligence layer.
 * Strictly observational — never claims causation. Built on R75 visibility.
 *
 * Returns:
 *   - effectiveness: per-artifact before/after deltas with confidence buckets
 *   - intelligence:  visibility metrics joined with deltas + transparent flags
 *
 * Gated by `canViewInScopeTalent`. Independent from Connect / scheduling /
 * talent analytics hooks.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEnterpriseScope } from './useEnterpriseScope';
import type { InScopeScopeType } from './useInScopeTalent';

/** Constant — kept here so it stays inspectable, not magic. */
export const BEFORE_AFTER_N = 5;

export type ConfidenceLevel = 'low' | 'medium' | 'high';

export interface ScopeTrainingEffectivenessRow {
  artifact_id: string;
  artifact_title: string;
  artifact_type: string;
  sample_size: number;
  avg_efficiency_delta: number | null;
  avg_comeback_delta: number | null;
  avg_diagnostic_accuracy_delta: number | null;
  confidence_level: ConfidenceLevel;
  last_computed_at: string;
}

export interface ScopeArtifactIntelligenceRow {
  artifact_id: string;
  artifact_title: string;
  artifact_type: string;
  total_uses: number;
  unique_technicians: number;
  sample_size: number;
  efficiency_delta: number | null;
  comeback_delta: number | null;
  diagnostic_delta: number | null;
  confidence_level: ConfidenceLevel;
  last_used_at: string | null;
  flags: string[];
}

interface UseScopeTrainingEffectivenessArgs {
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
}

export function useScopeTrainingEffectiveness({
  scopeType,
  scopeId,
}: UseScopeTrainingEffectivenessArgs) {
  const { canViewInScopeTalent } = useEnterpriseScope();
  const enabled = canViewInScopeTalent && !!scopeType && !!scopeId;

  const effectivenessQuery = useQuery({
    queryKey: ['scope-training-effectiveness', scopeType, scopeId, BEFORE_AFTER_N],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_scope_training_effectiveness' as any,
        {
          p_scope_type: scopeType!,
          p_scope_id: scopeId!,
          p_window_size: BEFORE_AFTER_N,
        } as any,
      );
      if (error) throw error;
      return ((data ?? []) as unknown) as ScopeTrainingEffectivenessRow[];
    },
    enabled,
  });

  const intelligenceQuery = useQuery({
    queryKey: ['scope-artifact-intelligence', scopeType, scopeId, BEFORE_AFTER_N],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_scope_artifact_intelligence' as any,
        {
          p_scope_type: scopeType!,
          p_scope_id: scopeId!,
          p_window_size: BEFORE_AFTER_N,
        } as any,
      );
      if (error) throw error;
      return ((data ?? []) as unknown) as ScopeArtifactIntelligenceRow[];
    },
    enabled,
  });

  return {
    effectiveness: effectivenessQuery.data ?? [],
    intelligence: intelligenceQuery.data ?? [],
    isLoading: effectivenessQuery.isLoading || intelligenceQuery.isLoading,
    error: (effectivenessQuery.error ?? intelligenceQuery.error) as Error | null,
  };
}
